type SparklineProps = {
  data: (number | null | undefined)[];
  color?: string;
  height?: number;
};

// A minimal trend indicator for stat cards: de-emphasized line, current value
// called out with an end-dot in the accent color, no axes/gridlines/legend -
// a single series doesn't need one (see dataviz skill, marks-and-anatomy.md).
// Stretches to the full width of its container (preserveAspectRatio="none")
// so it fills the space in the card rather than sitting as a small fixed-size
// chip; `width` below is just the internal coordinate resolution, not a
// rendered pixel size.
const Sparkline = ({ data, color = "#98d8ff", height = 40 }: SparklineProps) => {
  const width = 300;
  const values = data.filter(
    (v): v is number => v != null && Number.isFinite(v)
  );

  if (values.length < 2) {
    return null;
  }

  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;

  const padX = 6;
  const padY = 5;
  const plotW = width - padX * 2;
  const plotH = height - padY * 2;

  const points = values.map((v, i) => {
    const x = padX + (i / (values.length - 1)) * plotW;
    const y = padY + plotH - ((v - min) / range) * plotH;
    return [x, y];
  });

  const linePath = points
    .map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`)
    .join(" ");

  const areaPath = `${linePath} L${points[points.length - 1][0].toFixed(1)},${(
    height - padY
  ).toFixed(1)} L${points[0][0].toFixed(1)},${(height - padY).toFixed(1)} Z`;

  const [lastX, lastY] = points[points.length - 1];

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      style={{ width: "100%", height, display: "block" }}
      role="img"
      aria-label={`Trend over the last ${values.length} days`}
    >
      <path d={areaPath} fill={color} opacity={0.1} stroke="none" />
      <path
        d={linePath}
        fill="none"
        stroke={color}
        strokeWidth={2}
        strokeOpacity={0.55}
        strokeLinejoin="round"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
      <circle cx={lastX} cy={lastY} r={4} fill={color} vectorEffect="non-scaling-stroke" />
    </svg>
  );
};

export default Sparkline;
